import _ from 'lodash';
import moment from 'moment';
import { ActionTypes } from '../actions/cardProjectActions';
import {
  invitationSteps,
  saveTheDateSteps,
  postcardSaveTheDateSteps,
  thankYouSteps,
  menuSteps,
  detailSteps,
  programSteps
} from '../util/customizationNavigation';


const ORDERED_CUSTOMIZATION_TYPES = ['INVITATION', 'SAVE_THE_DATE', 'THANK_YOU', 'MENU', 'DETAILS', 'PROGRAM', 'RSVP', 'RSVP_ENVELOPE', 'INVITATION_ENVELOPE', 'ENVELOPE'];

const customizationMapper = (customization) => {
  const { elementsByUUID, pages } = customization.pages.reduce((result, page) => {
    const { elements, ...pageFields } = page;
    // handle filtering of pages where activeAt is null, these extra pages are used during toggling meal options and other
    // similar operations but should be filtered out
    if (pageFields.active_at) {
      const sortedElementUUIDs = elements
        .sort((a, b) => a.x - b.x) // sort element uuids by x values ascending so that form fields display from left to right
        .sort((a, b) => a.y - b.y) // then sort by y value ascending so that form fields display form fields display in same order that elements appear vertically
        .map(element => element.uuid);
      const formattedPage = {
        ...pageFields,
        elementUUIDs: sortedElementUUIDs,
      };
      result.pages.push(formattedPage);
      elements.forEach((element) => {
        result.elementsByUUID[element.uuid] = { // eslint-disable-line
          ...element,
          page: page.page_number,
          font_id: (element && element.font && element.font.id && element.content_type && element.content_type === 'TEXT') ? element.font.id : null,
        }; // eslint-disable-line
      });
    }
    return result;
  }, { elementsByUUID: {}, pages: [] });
  const formattedCustomization = {
    ...customization,
    name: customization.name,
    elementsByUUID,
    pages: _.sortBy(pages, page => page.page_number), // pages in response are not guaranteed to be ordered
  };

  return formattedCustomization;
};

function orderMapper(order) {
  return {
    createdAt: order.created_at,
    orderStatus: order.order_status,
    splitOrderID: order.split_order_id,
    projects: order.projects.map(project => ({
      projectUUID: project.project_uuid,
      projectName: project.project_name,
      completedAt: project.completed_at,
      quantity: project.quantity,
      primaryCustomization: _.find(project.customizations, customization => customization.type === 'INVITATION' || customization.type === 'SAVE_THE_DATE'),
      orderUUID: project.split_order_id,
      customizations: project.customizations.map((c) => { // eslint-disable-line
        // if (c.catalog_item_view) {
        return {
          customizationUUID: c.project_customization_uuid,
          type: c.type,
          name: c.name,
          quantity: c.quantity,
          renders: c.renders,
          // displayOrder: c.display_order,
          imageUrls: c.catalog_item_view.image_urls,
          // skuId: c.catalog_item_view.object_id,
          priceCents: c.catalog_item_view.price_cents,
          color: c.catalog_item_view.product_option_values.color,
          colorLabel: c.catalog_item_view.product_option_value_labels.color,
          paperType: c.catalog_item_view.product_option_values['paper-type'],
          reversePrinting: c.catalog_item_view.product_option_values['reverse-printing'],
          envelopeLining: c.catalog_item_view.product_option_values['envelope-lining'],
          envelopeLiningColor: c.catalog_item_view.product_option_value_labels['envelope-lining'],
          shape: c.catalog_item_view.product_option_values.silhouette,
          size: c.catalog_item_view.product_option_values.size
        };
      }).sort((a, b) => ORDERED_CUSTOMIZATION_TYPES.indexOf(a.type) - ORDERED_CUSTOMIZATION_TYPES.indexOf(b.type))
    }))
  };
}

function draftsMapper(drafts) {
  const filteredDrafts = drafts.filter(draft => draft.customizations.length > 0);
  const formattedDrafts = filteredDrafts.map(p => ({
    projectUUID: p.project_uuid,
    projectName: p.project_name,
    customizations: p.customizations.sort((a, b) => ORDERED_CUSTOMIZATION_TYPES.indexOf(a.type) - ORDERED_CUSTOMIZATION_TYPES.indexOf(b.type)),
    primaryCustomization: _.find(p.customizations, customization => (
      customization.type === 'INVITATION'
      || customization.type === 'SAVE_THE_DATE'
      || customization.type === 'THANK_YOU'
      || customization.type === 'MENU'
      || customization.type === 'DETAILS'
      || customization.type === 'PROGRAM'
    ))
  }));
  // try to sort by edited_at, because that is only set when user makes an edit
  // if edited_at is null (no edits) then fallback to updated_at
  const sortedDrafts = _.sortBy(formattedDrafts, draft => (
    draft.primaryCustomization.edited_at
      ? -moment(draft.primaryCustomization.edited_at).valueOf()
      : -moment(draft.primaryCustomization.updated_at).valueOf()
  ));
  const groupedDrafts = sortedDrafts.reduce((result, draft) => {
    result.count += 1; // eslint-disable-line
    if (_.has(result, draft.projectName)) {
      result[draft.projectName].push(draft);
    } else {
      result.orderedNames.push(draft.projectName);
      result[draft.projectName] = [draft]; // eslint-disable-line
    }
    return result;
  }, { orderedNames: [], count: 0 });
  return { groupedDrafts, drafts: formattedDrafts };
}

const fontVariationsMapper = (fonts, fontFamily) => (
  _.reduce(fonts, (result, font) => {
    if (font.font_family === fontFamily) {
      result.push({
        id: font.id,
        displayName: font.display_name.replace(fontFamily, '').trim(),
        fontWeight: font.font_weight,
        fontStyle: font.font_style,
        fontFamily: font.font_family
      });
    }
    // Here we split the list of variation on regular and non regular
    const splittedFonts = _.partition(result, { fontWeight: 400 });
    // Brings "Regular" to the top as default and then the rest
    const topFonts = _.sortBy(splittedFonts[0], topFont => (topFont.displayName === 'Regular' ? 0 : 1));
    const regularFonts = _.sortBy(splittedFonts[1], ['fontWeight']);
    // Return a merge of both lists keeping custom order
    return _.union(topFonts, regularFonts);
  }, [])
);

const fontsMapper = (fonts) => {
  /**
   * Here we split the font list to separate recommended from not recommended
   * then we filter uniques from the not recommended list
   * then we merge them keeping order so we have recommended on top
   * once we have a proper list we reduce them so as to fill a new array with needed values
   * TODO: see if we can chain lodash methods https://lodash.com/docs/4.17.11#chain
   */
  const splittedResult = _.partition(fonts, { template_referenced: true });
  const uniqueFonts = _.uniqBy(splittedResult[1], 'font_family');
  const fontList = _.union(splittedResult[0], uniqueFonts);
  const { mappedFonts } = _.reduce(fontList, (result, font) => {
    // Only add fonts not in result array
    if (!_.find(result.mappedFonts, { name: font.font_family })) {
      result.mappedFonts.push({
        class: font.font_family.replace(/\s/g, '-').toLowerCase(),
        id: font.id,
        isGlobal: font.globally_available,
        name: font.font_family,
        recommended: font.template_referenced,
        variations: fontVariationsMapper(fonts, font.font_family),
      });
    }
    return result;
  }, { mappedFonts: [] });

  return mappedFonts;
};

const initialState = {
  projectUUID: null,
  projectType: null,
  splitOrderId: null,
  customizations: {},
  fonts: {
    mapped: [],
    raw: []
  },
  // skus: {},
  customizationTypeToUUID: {},
  activeField: null,
  isFetching: false,
  steps: {},
  size: null,
  busy: false,
  drafts: {
    drafts: [],
    groupedDrafts: {
      orderedNames: [],
      count: 0,
    }
  },
  orders: [],
  isAddingToCart: false
};

function cardProjectReducer(state = initialState, action = {}) {
  switch (action.type) {
    case ActionTypes.REQUEST_PROJECT: {
      return { ...state, isFetching: true };
    }
    case ActionTypes.RECEIVE_PROJECT: {
      const { cardProject } = action.payload;
      const { id, uuid, name, split_order_id: splitOrderId, account_id: accountId } = cardProject;

      let projectType;
      if (_.find(cardProject.customizations, { type: 'SAVE_THE_DATE' })) {
        projectType = 'SAVE_THE_DATE';
      } else if (_.find(cardProject.customizations, { type: 'INVITATION' })) {
        projectType = 'INVITATION';
      } else if (_.find(cardProject.customizations, { type: 'THANK_YOU' })) {
        projectType = 'THANK_YOU';
      } else if (_.find(cardProject.customizations, { type: 'MENU' })) {
        projectType = 'MENU';
      } else if (_.find(cardProject.customizations, { type: 'DETAILS' })) {
        projectType = 'DETAILS';
      } else if (_.find(cardProject.customizations, { type: 'PROGRAM' })) {
        projectType = 'PROGRAM';
      }

      // const projectType = _.find(cardProject.customizations, { type: 'SAVE_THE_DATE' }) ? 'SAVE_THE_DATE' : 'INVITATION';
      const customizations = _.reduce(cardProject.customizations, (result, customization) => ({
        ...result,
        [customization.uuid]: customizationMapper(customization)
      }), {});
      const customizationTypeToUUID = _.reduce(cardProject.customizations, (result, customization) => {
        // IMPORTANT: this is a hack use during sku migration to get around multiple customizations with
        // type: ENVELOPE - need to come up with something better
        if (customization.type === 'ENVELOPE') {
          if (/rsvp/i.test(customization.name)) {
            // console.log('is RSVP: ', customization.name);
            result.RSVP_ENVELOPE = customization.uuid; // eslint-disable-line
          } else {
            result.INVITATION_ENVELOPE = customization.uuid; // eslint-disable-line
            // console.log('is INVITATION: ', customization.name);
          }
        }
        result[customization.type] = customization.uuid; // eslint-disable-line
        return result;
      }, {});

      const leadCustomization = _.find(cardProject.customizations, { type: projectType });
      let isPostCard;
      let size;
      if (leadCustomization) {
        if (leadCustomization.variation && leadCustomization.variation.option_values) {
          // for post sku-migration projects, we can just look at the variation.option_values of the
          // lead customization, which has a size field - this is all that will be needed after
          // sunsetting pre sku-migration projects
          const { variation } = leadCustomization;
          size = variation.option_values.size;
          isPostCard = variation.option_values.size === 'postcard';
        } else if (leadCustomization.type === 'INVITATION') {
          // we only offer invitations in 5x7 size right now
          size = '5x7';
          isPostCard = false;
        } else if (leadCustomization.type === 'SAVE_THE_DATE') {
          const projectHasEnvelope = (_.findIndex(cardProject.customizations, { type: 'INVITATION_ENVELOPE' }) > -1) || (_.findIndex(cardProject.customizations, { type: 'ENVELOPE' }) > -1);
          if (projectHasEnvelope) {
            const { width, height } = leadCustomization.pages[0];
            // the longest side of 5x7 cards is greater than 500 pts
            if (Math.max(width, height) > 500) {
              size = '5x7';
              isPostCard = false;
            } else {
              size = 'petite';
              isPostCard = false;
            }
          } else {
            // for projects of type SAVE_THE_DATE, the only one without a customization of
            // type ENVELOPE or INVITATION_ENVELOPE is postcard
            size = 'postcard';
            isPostCard = true;
          }
        }
      }
      // TODO: clean this up - find way to calculate navigation using customizations instead of using
      // hardcoded values
      let steps = [];
      if (projectType === 'INVITATION') {
        steps = invitationSteps;
      } else if (projectType === 'SAVE_THE_DATE') {
        if (isPostCard) {
          steps = postcardSaveTheDateSteps;
        } else {
          steps = saveTheDateSteps;
        }
      } else if (projectType === 'THANK_YOU') {
        steps = thankYouSteps;
      } else if (projectType === 'MENU') {
        steps = menuSteps;
      } else if (projectType === 'DETAILS') {
        steps = detailSteps;
      } else if (projectType === 'PROGRAM') {
        steps = programSteps;
      }

      return {
        ...state,
        id,
        projectUUID: uuid,
        projectType,
        name,
        splitOrderId,
        accountId,
        customizations,
        customizationTypeToUUID,
        isFetching: false,
        steps,
        size,
      };
    }
    case ActionTypes.REQUEST_CUSTOMIZATION: {
      return { ...state };
    }
    case ActionTypes.RECEIVE_CUSTOMIZATION: {
      const { customization } = action.payload;
      let customizationType = customization.type;
      // IMPORTANT: this is a hack use during sku migration to get around multiple customizations with
      // type: ENVELOPE - need to come up with something better
      if (customization.type === 'ENVELOPE') {
        if (/rsvp/i.test(customization.name)) {
          customizationType = 'RSVP_ENVELOPE';
        } else if (/save the date/i.test(customization.name)) {
          customizationType = 'INVITATION_ENVELOPE';
        } else if (/thank you/i.test(customization.name)) {
          customizationType = 'INVITATION_ENVELOPE';
        }
      }
      return {
        ...state,
        customizations: {
          ...state.customizations,
          [customization.uuid]: customizationMapper(customization),
        },
        customizationTypeToUUID: {
          ...state.customizationTypeToUUID,
          [customizationType]: customization.uuid
        },
      };
    }
    case ActionTypes.RECEIVE_CUSTOMIZATION_DISABLED: {
      const { uuid } = action.payload;
      return {
        ...state,
        customizations: {
          ...state.customizations,
          [uuid]: {
            ...state.customizations[uuid],
            deleted_at: true
          }
        }
      };
    }
    case ActionTypes.RECEIVE_ADD_TEXT_ELEMENT: {
      // set activeField to new element / insert in 'elementsByUUID' & 'pages.elementUUIDs'
      const { element, customizationUUID, pageuuid } = action.payload;
      const activeCustomization = state.customizations[customizationUUID];
      const newElementUUID = element.uuid;
      return {
        ...state,
        activeField: element.uuid,
        customizations: {
          ...state.customizations,
          [customizationUUID]: {
            ...activeCustomization,
            elementsByUUID: {
              ...activeCustomization.elementsByUUID,
              [newElementUUID]: element
            },
            pages: activeCustomization.pages.map((page) => {
              if (page.uuid === pageuuid) {
                return {
                  ...page,
                  elementUUIDs: [
                    ...page.elementUUIDs,
                    newElementUUID
                  ]
                };
              }
              return page;
            })
          }
        }
      };
    }

    // TODO: this could be named in a more generic way - RECEIVE_ELEMENT or something similar
    case ActionTypes.RECEIVE_DELETE_ELEMENT: {
      const { customizationUUID, elementUUID: deletedElementID } = action.payload;
      const activeCustomization = state.customizations[customizationUUID];
      const newElementsByUUID = Object.assign({}, activeCustomization.elementsByUUID);
      delete newElementsByUUID[deletedElementID];
      return {
        ...state,
        // remove focus
        activeField: null,
        // remove element from 'pages' & 'elementsByUUID'
        customizations: {
          ...state.customizations,
          [customizationUUID]: {
            ...activeCustomization,
            elementsByUUID: newElementsByUUID,
            pages: activeCustomization.pages.map((page) => {
              const filteredElements = page.elementUUIDs.filter(elemUUID => elemUUID !== deletedElementID);
              return { ...page, elementUUIDs: filteredElements };
            })
          }
        }
      };
    }
    case ActionTypes.RECEIVE_DELETE_ELEMENT_IMAGE: {
      const { customizationUUID, elementUUID, element } = action.payload;
      return {
        ...state,
        customizations: {
          ...state.customizations,
          [customizationUUID]: {
            ...state.customizations[customizationUUID],
            elementsByUUID: {
              ...state.customizations[customizationUUID].elementsByUUID,
              [elementUUID]: element
            }
          }
        }
      };
    }
    case ActionTypes.SET_ACTIVE_FIELD: {
      const { activeField } = action.payload;
      return { ...state, activeField };
    }
    case ActionTypes.REQUEST_DRAFTS: {
      return {
        ...state,
        busy: true
      };
    }
    case ActionTypes.RECEIVE_DRAFTS: {
      const { groupedDrafts, drafts } = draftsMapper(action.payload.drafts);
      // console.log('groupedDrafts: ', groupedDrafts);
      // console.log('drafts: ', drafts);
      return {
        ...state,
        drafts: {
          ...state.drafts,
          drafts,
          groupedDrafts,
          rawDrafts: action.payload.drafts, // used when deleting a draft
        },
        busy: false,
      };
    }
    case ActionTypes.RECEIVE_DELETE_PROJECT: {
      const { projectUUID } = action.payload;
      // console.log('drafts before: ', state.drafts.drafts);
      // console.log('groupedDrafts before: ', state.drafts.groupedDrafts);
      // console.log('rawDrafts: ', state.drafts.rawDrafts);
      const filteredRawDrafts = state.drafts.rawDrafts.filter(draft => draft.project_uuid !== projectUUID);
      const { groupedDrafts, drafts } = draftsMapper(filteredRawDrafts);
      // console.log('filteredDrafts: ', filteredRawDrafts);
      // console.log('drafts after: ', drafts);
      // console.log('groupedDrafts after: ', groupedDrafts);
      return {
        ...state,
        drafts: {
          ...state.drafts,
          rawDrafts: filteredRawDrafts,
          drafts,
          groupedDrafts,
        }
      };
    }
    case ActionTypes.REQUEST_ORDERS: {
      return { ...state, busy: true };
    }
    case ActionTypes.RECEIVE_ORDERS: {
      const { orders } = action.payload;
      const formattedOrders = orders.map(order => orderMapper(order));
      const sortedOrders = formattedOrders.sort((a, b) => (
        moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
      ));

      return {
        ...state,
        orders: sortedOrders,
        busy: false };
    }
    case ActionTypes.RECEIVE_CANCELED_ORDER: {
      const { order } = action.payload;
      const formattedOrder = orderMapper(order);
      const updatedOrders = state.orders.map((currentOrder) => {
        if (currentOrder.splitOrderID === formattedOrder.splitOrderID) {
          return formattedOrder;
        }
        return currentOrder;
      });
      return { ...state, orders: updatedOrders };
    }
    // Customization fonts actions
    case ActionTypes.GET_CUSTOMIZATION_FONTS_REQUEST: {
      return { ...state, isFetching: true };
    }
    case ActionTypes.GET_CUSTOMIZATION_FONTS_SUCCESS: {
      const { fonts } = action.payload;
      const mappedFonts = fontsMapper(fonts);
      return {
        ...state,
        isFetching: false,
        fonts: {
          mapped: mappedFonts,
          raw: fonts
        }
      };
    }
    case ActionTypes.GET_CUSTOMIZATION_FONTS_FAILURE: {
      return { ...state, isFetching: false, fonts: [] };
    }
    case ActionTypes.REQUEST_ADD_PROJECT_TO_CART: {
      return { ...state, isAddingToCart: true };
    }
    case ActionTypes.RECEIVE_ADD_PROJECT_TO_CART: {
      return { ...state, isAddingToCart: false };
    }
    default: {
      return { ...state };
    }
  }
}

export default cardProjectReducer;



// WEBPACK FOOTER //
// ./src/cards/reducers/cardProject.js